import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { AlertCircle, CheckCircle2 } from "lucide-react";
import {API} from "@/App";

function ResetPassword() {
    const { token } = useParams(); // URL param
    const [searchParams] = useSearchParams();
    const username = searchParams.get("username"); // query param

    const navigate = useNavigate();
    const [password, setPassword] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [status, setStatus] = useState(""); // "success" | "error"
    const [message, setMessage] = useState("");

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!password) return;

        setIsLoading(true);
        setStatus("");
        setMessage("");

        try {
            const res = await fetch(`${API}/auth/resetPassword/${token}`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ password }),
            });

            if (res.ok) {
                setStatus("success");
                setMessage("Heyyy! Password reset successful. Redirecting to login...");
                setTimeout(() => navigate("/login"), 2000);
            } else {
                const data = await res.json();
                setStatus("error");
                setMessage(data?.detail || "Failed to reset password.");
            }
        } catch {
            setStatus("error");
            setMessage("Network error. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="flex min-h-screen items-center justify-center">
            <Card className="w-[400px]">
                <CardHeader>
                    <CardTitle>Reset Password</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="mb-4">
                        <p>
                            Resetting password for: <strong>{username || "Unknown User"}</strong>
                        </p>
                    </div>
                    <form onSubmit={handleSubmit} className="grid gap-4">
                        <div className="grid gap-2">
                            <Label htmlFor="password">New Password</Label>
                            <Input
                                id="password"
                                type="password"
                                placeholder="••••••••"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                disabled={isLoading}
                                required
                            />
                        </div>

                        {status === "success" && (
                            <div className="flex items-center gap-x-2 rounded-md bg-green-100 p-3 text-sm text-green-700">
                                <CheckCircle2 className="h-4 w-4" />
                                <p>{message}</p>
                            </div>
                        )}

                        {status === "error" && (
                            <div className="flex items-center gap-x-2 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                                <AlertCircle className="h-4 w-4" />
                                <p>{message}</p>
                            </div>
                        )}

                        <Button type="submit" disabled={isLoading}>
                            {isLoading ? "Resetting..." : "Reset Password"}
                        </Button>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
}

export default ResetPassword;
